import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchUserReferrals, clearData } from 'store/referralSlice';
import { showToast } from 'store/toastSlice';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import { referralService } from 'components/api/referral';
import AddReferralModal from './AddReferralModal';
import styles from '../userDetails.module.scss';

interface UserReferralsProps {
  userId: string;
}

const UserReferrals: React.FC<UserReferralsProps> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, total, isLoading, params, referredBy } = useAppSelector((state) => state.referral);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleAddReferral = async (selectedUser: any, selectedCode: any) => {
    try {
      await referralService.addUserReferral(userId, {
        childUserId: selectedUser.id || selectedUser.userId,
        campaignId: selectedCode.id || selectedCode.campaignId,
      });
      dispatch(showToast({ text: 'Referral added successfully', type: 'success' }));
      setIsModalOpen(false);
      loadData();
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to add referral',
          type: 'error',
        }),
      );
    }
  };

  const loadData = useCallback(() => {
    dispatch(fetchUserReferrals({ userId, params }));
  }, [dispatch, userId, params]);

  useEffect(() => {
    loadData();
    return () => {
      dispatch(clearData());
    };
  }, [loadData]);

  const columns: GridColDef[] = [
    { field: 'referralId', headerName: 'ID', width: 70 },
    {
      field: 'username',
      headerName: 'Username',
      width: 150,
      renderCell: (params) => (
        <Typography
          onClick={() => navigate(`/clients/${params.row.userId}`)}
          sx={{
            color: 'var(--accent-blue)',
            cursor: 'pointer',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: 'email', headerName: 'Email', width: 220 },
    { field: 'campaignName', headerName: 'Campaign', width: 130 },
    { field: 'campaignCode', headerName: 'Code', width: 100 },
    { field: 'commission', headerName: 'Commission', width: 100 },
    { field: 'deposit', headerName: 'Deposit', width: 100 },
    { field: 'wager', headerName: 'Wager', width: 100 },
    {
      field: 'registeredAt',
      headerName: 'Registration Date',
      width: 180,
      renderCell: (params) => (params.row.registeredAt ? formatDate(params.row.registeredAt) : '-'),
    },
  ];

  if (isLoading && !data.length) {
    return (
      <Box className={styles.loadingWrapper}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box className={styles.tabContent}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Referrals
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Add filters if needed in the future */}
        </Box>
      </Box>

      {referredBy ? (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'var(--bg-panel-secondary)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
            Referred By:
          </Typography>
          <Typography
            onClick={() => navigate(`/clients/${referredBy.owner.userId}`)}
            sx={{
              color: 'var(--accent-blue)',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {referredBy.owner.username}
          </Typography>
          <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            ({referredBy.owner.email})
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'var(--bg-panel-secondary)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            opacity: 0.8,
          }}
        >
          <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            User was not referred by anyone.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => setIsModalOpen(true)}
          sx={{
            bgcolor: '#2a8039',
            '&:hover': { bgcolor: '#1e662a' },
            textTransform: 'none',
          }}
        >
          Add referrals for this user
        </Button>
      </Box>

      {data.length > 0 ? (
        <>
          <Box sx={{ width: '100%', mt: 2 }}>
            <DataGrid
              rows={data || []}
              rowCount={total || 0}
              getRowId={(row: any) => row.referralId}
              columns={columns}
              loading={isLoading}
              sx={commonGridStyles}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            mt: 4,
            p: 4,
            textAlign: 'center',
            backgroundColor: 'var(--bg-panel-secondary)',
            borderRadius: '8px',
            border: '2px dashed var(--border-color)',
          }}
        >
          <Typography sx={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
            No referrals found for this user.
          </Typography>
        </Box>
      )}

      <AddReferralModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddReferral}
        currentUserId={userId}
      />
    </Box>
  );
};

export default UserReferrals;
